import { Inject, Injectable, Optional } from '../components/dependency-injection/decorators';
import { EventQueue } from '../components/event-queue';
import { Logger } from '../components/logger/logger';
import { EventRegisterBodyDto, ProcessNpiBodyDto, RecommendBodyDto } from '../dto/recs.dto';
import type { HttpClientInterface } from '../interfaces/components/http-client/http-client.interface';
import { PublicNpi, PublicRecommendationItem, PublicSettings } from '../types';
import { publicNpiListSchema, publicRecommendationItemListSchema, publicSettingsSchema } from '../utils/schema';

@Injectable()
export class ApiClient {
  private readonly eventQueue: EventQueue;

  public constructor (
    @Inject('backendHttpClient') private readonly httpClient: HttpClientInterface,
    @Optional() private readonly logger?: Logger,
  ) {
    this.eventQueue = new EventQueue({
      maxQueueSize: 20,
      timeout: 2000,
      callback: this.eventQueueCallback.bind(this),
    });
  }

  public async getRecommendations (adSlotIdentifier: string, payload: RecommendBodyDto):
  Promise<PublicRecommendationItem[]> {
    const request = this.httpClient.createRequest(
      'POST',
      `/ad-slots/${adSlotIdentifier}/recommendations`,
      {
        json: payload,
      },
    );

    const recommendationItems = await this.httpClient
      .sendRequest(request)
      .then((response) => response.json<PublicRecommendationItem[]>());

    try {
      return publicRecommendationItemListSchema.parse(recommendationItems);
    } catch (error) {
      this.logger?.error('Unable to parse public recommendation items', error);
      throw new Error('Unable to parse public recommendation items', { cause: error });
    }
  }

  public async getSettings (adSetIdentifier: string): Promise<PublicSettings> {
    const request = this.httpClient.createRequest('GET', `/ad-sets/${adSetIdentifier}/settings`);

    const publicSettings = await this.httpClient
      .sendRequest(request)
      .then((response) => response.json<PublicSettings>());

    try {
      return publicSettingsSchema.parse(publicSettings);
    } catch (error) {
      this.logger?.error('Unable to parse public settings', error);
      throw new Error('Unable to parse public settings', { cause: error });
    }
  }

  public async processNpi (data: ProcessNpiBodyDto, adSetIdentifier: string): Promise<PublicNpi[]> {
    const request = this.httpClient.createRequest('POST', `/ad-sets/${adSetIdentifier}/npi`, {
      json: data,
    });

    const npis = await this.httpClient
      .sendRequest(request)
      .then((response) => {
        if (response.getStatusCode() >= 400) {
          this.logger?.error(`Unable to process npi. Status: ${response.getStatusCode()}, ${response.getReasonPhrase()}`);
          throw new Error('Unable to process npi');
        }
        return response;
      })
      .then((response) => response.json<PublicNpi[]>())
      .catch(() => []);

    try {
      return publicNpiListSchema.parse(npis);
    } catch (error) {
      this.logger?.error('Unable to parse public npis', error);
      throw new Error('Unable to parse public npis', { cause: error });
    }
  }

  public async registerEvent (adSlotIdentifier: string, payload: EventRegisterBodyDto): Promise<void> {
    this.eventQueue.push({ adSlotIdentifier, payload });
  }

  private async eventQueueCallback (items: { adSlotIdentifier: string, payload: EventRegisterBodyDto }[]): Promise<void> {
    const groupedItems = items.reduce((acc, obj) => {
      if (!acc.has(obj.adSlotIdentifier)) {
        acc.set(obj.adSlotIdentifier, []);
      }

      acc.get(obj.adSlotIdentifier)?.push(obj);

      return acc;
    }, new Map<string, typeof items[number][]>());

    const promises: Promise<void>[] = [];

    for (const [ adSlotIdentifier, queueItems ] of groupedItems.entries()) {
      const request = this.httpClient.createRequest(
        'POST',
        `/ad-slots/${adSlotIdentifier}/events`,
        {
          json: queueItems.map((item) => item.payload),
        },
      );

      promises.push(this.httpClient.sendRequest(request).then((response) => void 0));
    }

    await Promise.all(promises);
  }
}
